import { api } from './apiClient';

// ---- Types (kept intentionally light; the API is the source of truth) ----
export interface Machine {
  id: number;
  machine_code: string;
  name: string;
  status: 'RUNNING' | 'IDLE' | 'DOWN' | 'MAINTENANCE' | 'OFFLINE';
  capacity_per_hour: number | null;
  warehouse_id: number | null;
  commissioned_on: string | null;
}

export interface WorkOrder {
  id: number;
  wo_number: string;
  product_id: number | null;
  product_name?: string | null;
  machine_id: number | null;
  machine_name?: string | null;
  sales_order_id: number | null;
  bom_id?: number | null;
  routing_id?: number | null;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT' | null;
  notes?: string | null;
  planned_qty: number;
  produced_qty: number;
  rejected_qty: number;
  status: 'PLANNED' | 'RELEASED' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED';
  planned_start: string | null;
  planned_end: string | null;
  actual_start: string | null;
  actual_end: string | null;
}

export interface WorkOrderMaterial {
  id: number;
  work_order_id: number;
  product_id: number;
  product_name: string;
  product_code: string;
  unit_of_measure: string;
  planned_qty: number;
  issued_qty: number;
}

export interface ProductionEntry {
  id: number;
  work_order_id: number;
  entry_date: string;
  shift: string;
  produced_qty: number;
  rejected_qty: number;
  runtime_mins: number;
  planned_mins: number;
  // Declaration-depth fields (present on list + detail).
  good_qty?: number;
  scrap_qty?: number;
  rework_qty?: number;
  yield_percent?: number | null;
  downtime_mins?: number;
  downtime_reason?: string | null;
  batch_no?: string | null;
  auto_stock?: number | boolean;
  machine_id?: number | null;
  machine_name?: string | null;
  // Joined context from the enriched list query.
  wo_number?: string;
  product_id?: number | null;
  product_name?: string | null;
  product_code?: string | null;
  wo_planned_qty?: number;
}

export interface ProductionDeclarationInput {
  workOrderId: number;
  entryDate: string;
  shift?: string;
  machineId?: number;
  autoStock?: boolean;
  producedQty?: number;
  goodQty?: number;
  rejectedQty?: number;
  scrapQty?: number;
  reworkQty?: number;
  yieldPercent?: number;
  runtimeMins?: number;
  plannedMins?: number;
  inputQty?: number;
  expectedLossPercent?: number;
  lossReasonId?: number;
  downtimeMins?: number;
  downtimeReason?: string;
  powerUnits?: number;
  waterUnits?: number;
  fuelUnits?: number;
  utilityCost?: number;
  laborHours?: number;
  operatorCount?: number;
  batchNo?: string;
  supervisorId?: number;
  operatorId?: number;
  remarks?: string;
  consumption?: { productId: number; bomQty: number; actualQty: number; uom: string }[];
}

export interface QualityCheck {
  id: number;
  work_order_id: number | null;
  product_id: number | null;
  parameter: string;
  expected_value: string | null;
  actual_value: string | null;
  result: 'PASS' | 'FAIL' | 'REWORK';
  checked_at: string;
}

export interface WorkOrderDetail extends WorkOrder {
  materials: WorkOrderMaterial[];
  productionEntries: ProductionEntry[];
  qualityChecks: QualityCheck[];
}

export interface DowntimeLog {
  id: number;
  machine_id: number;
  reason: string;
  started_at: string;
  ended_at: string | null;
  duration_mins: number | null;
  notes: string | null;
}

export interface ManufacturingSummary {
  [key: string]: any;
}

// Light shapes for the BOM / Routing pickers used when creating a work
// order — full detail lives in BomPage.tsx / RoutingPage.tsx, this is just
// enough to populate an Autocomplete.
export interface BomOption {
  id: number;
  bom_code: string;
  product_id?: number;
  product_name?: string;
  version: number;
  is_default: boolean;
  status: string;
}

export interface RoutingOption {
  id: number;
  routing_code: string;
  product_id?: number;
  product_name?: string;
  version: number;
  is_default: boolean;
  status: string;
}

export interface IssueResult {
  workOrderId: number;
  issuedValue: number;
  journalEntryId: number | null;
}

export interface ReceiveResult {
  workOrderId: number;
  quantity: number;
  value: number;
  journalEntryId: number | null;
}

export interface EligibleSalesOrder {
  sales_order_id: number; order_number: string; customer_id: number | null; customer_name: string | null;
  product_id: number; product_name: string; product_code: string;
  ordered_qty: number; produced_qty: number; delivered_qty: number; pending_production: number;
}

export interface BlendEntry {
  id: number; blend_number: string; output_product_id: number; output_product_name?: string;
  output_batch_id: number | null; output_batch_number?: string; output_qty: number; input_qty: number;
  loss_qty: number | null; loss_percent: number | null; warehouse_id: number | null; warehouse_name?: string;
  blend_date: string; status: string;
  inputs?: { id: number; product_id: number; product_name?: string; batch_id: number | null; batch_number?: string; quantity: number; percentage: number | null }[];
}

export interface PackingEntry {
  id: number; packing_number: string; work_order_id: number | null; wo_number?: string;
  product_id: number; product_name?: string; product_code?: string; batch_id: number | null; batch_number?: string;
  quantity: number; uom: string | null; pack_size: number | null; num_packs: number | null;
  packing_material_id: number | null; packing_material_name?: string; packing_material_qty: number | null;
  warehouse_id: number | null; warehouse_name?: string; packing_date: string; status: string;
}

export interface MtoLine {
  product_id: number; product_name: string; product_code: string;
  planned_qty?: number; work_order_id?: number; wo_number?: string; reason?: string;
}
export interface MtoResult {
  salesOrderId: number; soNumber: string; preview: boolean; created: MtoLine[]; skipped: MtoLine[];
}

export const manufacturingService = {
  getSummary: (): Promise<ManufacturingSummary> => api.get('/manufacturing/summary'),

  getProductionControl: (): Promise<any> => api.get('/manufacturing/production-control'),

  getVisualFactory: (): Promise<any> => api.get('/manufacturing/visual-factory'),

  getOEE: (params?: { machineId?: number; from?: string; to?: string }): Promise<any> =>
    api.get('/manufacturing/oee', { params }),

  // Work centers (machines)
  getMachines: (status?: string): Promise<{ machines: Machine[]; count: number }> =>
    api.get('/manufacturing/machines', { params: status ? { status } : undefined }),
  createMachine: (data: {
    name: string; machineCode?: string; capacityPerHour?: number; status?: string;
    warehouseId?: number; commissionedOn?: string;
  }): Promise<{ machine: Machine }> =>
    api.post('/manufacturing/machines', data),
  updateMachine: (id: number, data: {
    name?: string; capacityPerHour?: number; warehouseId?: number; commissionedOn?: string;
  }): Promise<{ machine: Machine }> =>
    api.put(`/manufacturing/machines/${id}`, data),
  updateMachineStatus: (id: number, status: string): Promise<{ machine: Machine }> =>
    api.patch(`/manufacturing/machines/${id}/status`, { status }),

  // Work orders
  getWorkOrders: (params?: { status?: string; page?: number; limit?: number }): Promise<{ workOrders: WorkOrder[]; pagination: any }> =>
    api.get('/manufacturing/work-orders', { params }),
  getWorkOrder: (id: number): Promise<{ workOrder: WorkOrderDetail }> =>
    api.get(`/manufacturing/work-orders/${id}`),
  createWorkOrder: (data: {
    productId?: number; machineId?: number; bomId?: number; routingId?: number; salesOrderId?: number;
    plannedQty: number; plannedStart?: string; plannedEnd?: string; priority?: string; notes?: string; status?: string;
  }): Promise<{ workOrder: WorkOrderDetail }> =>
    api.post('/manufacturing/work-orders', data),
  updateWorkOrderStatus: (id: number, status: string): Promise<{ workOrder: WorkOrderDetail }> =>
    api.patch(`/manufacturing/work-orders/${id}/status`, { status }),
  // Lifecycle-aware editing: full edit only while PLANNED; amendment (reason required)
  // for RELEASED/IN_PROGRESS/ON_HOLD. Backend enforces both.
  updateWorkOrder: (id: number, data: {
    productId?: number; machineId?: number; bomId?: number; routingId?: number; salesOrderId?: number;
    plannedQty?: number; priority?: string; notes?: string; plannedStart?: string; plannedEnd?: string;
  }): Promise<{ workOrder: WorkOrderDetail }> =>
    api.put(`/manufacturing/work-orders/${id}`, data),
  amendWorkOrder: (id: number, data: {
    reason: string; machineId?: number; priority?: string; notes?: string;
    plannedQty?: number; plannedStart?: string; plannedEnd?: string;
  }): Promise<{ workOrder: WorkOrderDetail }> =>
    api.post(`/manufacturing/work-orders/${id}/amend`, data),
  getWorkOrderAmendments: (id: number): Promise<{ amendments: any[]; count: number }> =>
    api.get(`/manufacturing/work-orders/${id}/amendments`),
  releaseWorkOrder: (id: number): Promise<{ workOrder: WorkOrderDetail }> =>
    api.post(`/manufacturing/work-orders/${id}/release`, {}),
  getWorkOrderAvailability: (id: number): Promise<any> =>
    api.get(`/manufacturing/work-orders/${id}/availability`),

  // Materials + material issue / FG receipt (the integrated bit)
  getMaterials: (woId: number): Promise<{ materials: WorkOrderMaterial[]; count: number }> =>
    api.get(`/manufacturing/work-orders/${woId}/materials`),
  setMaterials: (woId: number, materials: { productId: number; plannedQty: number }[]): Promise<any> =>
    api.put(`/manufacturing/work-orders/${woId}/materials`, { materials }),
  issueMaterials: (woId: number, lines: { productId: number; quantity: number }[], opts?: { warehouseCode?: string; entryDate?: string }): Promise<IssueResult> =>
    api.post(`/manufacturing/work-orders/${woId}/issue-materials`, { lines, ...opts }),
  receiveFinishedGoods: (woId: number, quantity: number, opts?: { warehouseCode?: string; entryDate?: string }): Promise<ReceiveResult> =>
    api.post(`/manufacturing/work-orders/${woId}/receive-fg`, { quantity, ...opts }),

  // Production confirmation / declaration
  getProductionEntries: (params?: { workOrderId?: number; from?: string; to?: string }): Promise<{ productionEntries: ProductionEntry[]; count: number }> =>
    api.get('/manufacturing/production-entries', { params }),
  createProductionEntry: (data: ProductionDeclarationInput): Promise<{ productionEntry: ProductionEntry }> =>
    api.post('/manufacturing/production-entries', data),

  // Packing
  getPacking: (params?: { workOrderId?: number; from?: string; to?: string }): Promise<{ packings: PackingEntry[]; count: number }> =>
    api.get('/manufacturing/packing', { params }),
  createPacking: (data: {
    workOrderId?: number; productId: number; batchId?: number; quantity: number; uom?: string;
    packSize?: number; numPacks?: number; packingMaterialId?: number; packingMaterialQty?: number;
    warehouseId?: number; packingDate?: string; operatorId?: number; remarks?: string;
  }): Promise<{ packing: PackingEntry }> =>
    api.post('/manufacturing/packing', data),

  // Eligible sales orders for make-to-order (open-to-produce)
  getEligibleSalesOrders: (productId?: number): Promise<{ salesOrders: EligibleSalesOrder[]; count: number }> =>
    api.get('/manufacturing/eligible-sales-orders', { params: productId ? { productId } : undefined }),

  // Blending (optional)
  getBlends: (params?: { from?: string; to?: string }): Promise<{ blends: BlendEntry[]; count: number }> =>
    api.get('/manufacturing/blends', { params }),
  getBlend: (id: number): Promise<{ blend: BlendEntry }> => api.get(`/manufacturing/blends/${id}`),
  createBlend: (data: {
    outputProductId: number; outputBatchNo?: string; outputQty?: number; warehouseId: number;
    blendDate?: string; operatorId?: number; remarks?: string;
    inputs: { productId: number; batchId?: number; quantity: number }[];
  }): Promise<{ blend: BlendEntry }> =>
    api.post('/manufacturing/blends', data),

  // Make-to-order bridge (Sales Order -> Production Orders)
  previewProductionOrdersFromSO: (soId: number): Promise<MtoResult> =>
    api.get(`/manufacturing/sales-orders/${soId}/production-orders/preview`),
  generateProductionOrdersFromSO: (soId: number): Promise<MtoResult> =>
    api.post(`/manufacturing/sales-orders/${soId}/production-orders`, {}),

  // Quality
  getQualityChecks: (params?: { workOrderId?: number; result?: string }): Promise<{ qualityChecks: QualityCheck[]; count: number }> =>
    api.get('/manufacturing/quality-checks', { params }),
  createQualityCheck: (data: {
    workOrderId?: number; productId?: number; parameter: string;
    expectedValue?: string; actualValue?: string; result?: string;
  }): Promise<{ qualityCheck: QualityCheck }> =>
    api.post('/manufacturing/quality-checks', data),

  // Downtime
  getDowntime: (params?: { machineId?: number; from?: string; to?: string }): Promise<{ downtimeLogs: DowntimeLog[]; count: number }> =>
    api.get('/manufacturing/downtime', { params }),

  // BOM / Routing pickers (used when creating a work order)
  getBoms: (params?: { productId?: number; status?: string }): Promise<{ boms: BomOption[]; count: number }> =>
    api.get('/bom', { params }),
  getRoutings: (params?: { productId?: number; status?: string }): Promise<{ routings: RoutingOption[]; count: number }> =>
    api.get('/routings', { params }),
};

export default manufacturingService;
